import React, { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { getApiUrl, VENUES_ENABLED_FALLBACK } from '../config';

const POLL_MS = 45_000;

interface FeatureFlags {
  venues: boolean;
  // Validación de partidos en cancha y programa de premios. Separado de
  // `venues` a propósito: el panel de reservas sigue dormido.
  venue_rewards: boolean;
  [key: string]: boolean;
}

export const DEFAULT_FLAGS: FeatureFlags = { venues: false, venue_rewards: false };

interface FeatureFlagContextValue extends FeatureFlags {
  loading: boolean;
}

const defaultValue: FeatureFlagContextValue = { ...DEFAULT_FLAGS, loading: true };

const FeatureFlagContext = createContext<FeatureFlagContextValue>(defaultValue);

export const FeatureFlagProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [flags, setFlags] = useState<FeatureFlags>(DEFAULT_FLAGS);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    fetch(getApiUrl('/feature-flags'))
      .then((r) => r.json())
      .then((data) => {
        setFlags({ ...DEFAULT_FLAGS, ...data });
        setLoading(false);
      })
      .catch(() => {
        setFlags({ ...DEFAULT_FLAGS, venues: VENUES_ENABLED_FALLBACK });
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    load();
    const id = window.setInterval(load, POLL_MS);
    return () => window.clearInterval(id);
  }, [load]);

  useEffect(() => {
    const socketUrl =
      process.env.NODE_ENV === 'development' ? '' : process.env.REACT_APP_API_URL || '';
    let socket: Socket | null = null;
    try {
      socket = io(socketUrl, { transports: ['websocket', 'polling'], autoConnect: true });
      socket.on('featureFlagsUpdated', () => load());
    } catch {
      // sin socket.io en tests o entornos raros, sigue el polling
    }
    return () => {
      socket?.disconnect();
    };
  }, [load]);

  return (
    <FeatureFlagContext.Provider value={{ ...flags, loading }}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlags = () => useContext(FeatureFlagContext);
